import { forwardRef, memo, useMemo } from "react";

import { preloadCache } from "./preload";

import "./Icon.css";

import type { CSSProperties } from "react";
import type { CommonProps, SizeProp } from "../components";

export type IconVariantProp = "regular" | "solid" | "brand";

export interface IconProps extends CommonProps {
  color?: string;
  icon: string;
  variant?: IconVariantProp;
  size?: Extract<SizeProp, "sm" | "md" | "lg" | "xl" | "2xl"> | string;
}

export interface IconRawData {
  xml: string;
  viewBox: string;
}

export function getIconSizeVar(size: IconProps["size"]) {
  switch (size) {
    case "sm":
      return "var(--size-2)";
    case "md":
      return "var(--size-2-5)";
    case "lg":
      return "var(--size-3)";
    case "xl":
      return "var(--size-4)";
    case "2xl":
      return "var(--size-5)";
    default:
      return size;
  }
}

export const Icon = memo(
  forwardRef<SVGSVGElement, IconProps>(function IconImpl(
    { className, color, icon, size, style = {}, variant = "regular", ...rest },
    ref,
  ) {
    const customProperties = {
      "--icon-color": color,
      "--icon-size": getIconSizeVar(size),
    } as CSSProperties;

    // Render the preloaded icon if it exists
    const preloaded = useMemo(() => {
      const data = preloadCache[[variant, icon].join(":")];

      if (!data) return;

      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox={data.viewBox}
          dangerouslySetInnerHTML={{ __html: data.xml }}
        />
      );
    }, [icon, variant]);

    return (
      <bp-icon
        class={className}
        ref={ref}
        icon={[variant, icon].join(":")}
        style={{
          ...customProperties,
          ...style,
        }}
        {...rest}
      >
        {preloaded}
      </bp-icon>
    );
  }),
);

Icon.displayName = "Icon";
